import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/http';
import { authApi } from '@/api/endpoints';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/dashboard/plano')({
  validateSearch: (s: Record<string, unknown>) => ({
    upgraded: s.upgraded === '1' || s.upgraded === 1 ? true : false,
  }),
  component: PlanoPage,
});

const TIERS: ('FREE' | 'PRO' | 'PREMIUM')[] = ['FREE', 'PRO', 'PREMIUM'];

function PlanoPage() {
  const qc = useQueryClient();
  const search = useSearch({ from: '/dashboard/plano' }) as { upgraded?: boolean };

  const { data, isLoading } = useQuery({
    queryKey: ['my-plan'],
    queryFn: () => api.get('subscriptions/me').json<any>(),
  });

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: authApi.me,
  });

  const feeWaiverUntil: string | null = me?.therapistProfile?.feeWaiverUntil ?? null;
  const waiverDaysLeft = feeWaiverUntil
    ? Math.max(0, Math.ceil((new Date(feeWaiverUntil).getTime() - Date.now()) / 86_400_000))
    : 0;
  const inWaiver = waiverDaysLeft > 0;

  const upgrade = useMutation({
    mutationFn: (tier: 'PRO' | 'PREMIUM') => api.post('subscriptions/upgrade', { json: { tier } }).json<any>(),
    onSuccess: (res: any) => {
      if (res?.checkoutUrl) window.location.href = res.checkoutUrl;
      else qc.invalidateQueries({ queryKey: ['my-plan'] });
    },
  });

  const manage = useMutation({
    mutationFn: () => api.post('subscriptions/manage').json<{ url: string }>(),
    onSuccess: (res) => { if (res?.url) window.location.href = res.url; },
  });

  if (isLoading || !data) return <p className="text-slate-500 text-sm">Carregando...</p>;

  const currentTier = data.currentPlan ?? 'FREE';
  const catalog = data.catalog ?? {};
  const hasActive = !!data.hasActiveSubscription;
  const currentMeta = catalog[currentTier] ?? { name: currentTier, badge: null };

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Plano de assinatura</h1>
        <p className="text-slate-500 text-sm mt-1">
          Reduza a taxa da plataforma e ganhe destaque. Cancele a qualquer momento.
        </p>
      </div>

      {search.upgraded && (
        <Card className="border-teal-200 bg-teal-50/50">
          <CardContent className="p-4 text-sm text-teal-800">
            ✅ Assinatura ativada com sucesso. Pode levar alguns segundos pra aparecer aqui.
          </CardContent>
        </Card>
      )}

      {inWaiver && (
        <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100 px-5 py-4 flex items-start gap-3">
          <div className="text-2xl shrink-0">🎁</div>
          <div className="text-sm flex-1">
            <p className="font-bold text-amber-900">
              Promoção de adesão ativa — comissão zerada em todos os planos
            </p>
            <p className="text-xs mt-1 text-amber-800 leading-relaxed">
              Durante os próximos <strong>{waiverDaysLeft} dia{waiverDaysLeft === 1 ? '' : 's'}</strong>,
              a plataforma <strong>não cobra comissão</strong> por sessão, independente do plano que você
              escolher. As taxas abaixo passam a valer depois de{' '}
              <strong>{new Date(feeWaiverUntil!).toLocaleDateString('pt-BR')}</strong>.
              Mensalidade do PRO/PREMIUM, se contratada, segue cobrada normal.
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Plano atual</div>
            <div className="text-xl font-extrabold text-slate-900 mt-1 flex items-center gap-2">
              {currentMeta.name}
              {currentMeta.badge && (
                <span className="bg-coral-500 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                  {currentMeta.badge}
                </span>
              )}
            </div>
            {data.planEndsAt && (
              <div className="text-xs text-slate-500 mt-1">
                Renovação: {new Date(data.planEndsAt).toLocaleDateString('pt-BR')}
              </div>
            )}
          </div>
          {hasActive && (
            <Button variant="outline" onClick={() => manage.mutate()} disabled={manage.isPending}>
              {manage.isPending ? 'Abrindo...' : 'Gerenciar assinatura'}
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-3 gap-4 items-stretch">
        {TIERS.map((tier) => {
          const plan = catalog[tier];
          if (!plan) return null;
          const isCurrent = currentTier === tier;
          return (
            <Card
              key={tier}
              className={cn(
                'relative overflow-hidden flex flex-col h-full',
                plan.highlighted && !isCurrent && 'shadow-md',
                isCurrent && 'ring-2 ring-teal-500 shadow-lg',
              )}
            >
              {plan.highlighted && (
                <div className="bg-teal-500 text-white text-[10px] font-bold py-1.5 text-center uppercase tracking-widest">
                  ⭐ Mais popular
                </div>
              )}
              <CardContent className="p-6 flex flex-col flex-1">
                <div className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                  {plan.name}
                </div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold text-slate-900">
                    {plan.pricePerMonth === 0 ? 'Grátis' : `R$ ${plan.pricePerMonth}`}
                  </span>
                  {plan.pricePerMonth > 0 && (
                    <span className="text-xs text-slate-500">/mês</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  {inWaiver ? (
                    <>
                      Taxa da plataforma:{' '}
                      <span className="line-through text-slate-400">{plan.platformFeePercent}%</span>{' '}
                      <strong className="text-amber-700">0% — promoção de adesão</strong>
                    </>
                  ) : (
                    <>
                      Taxa da plataforma:{' '}
                      <strong className="text-slate-800">{plan.platformFeePercent}%</strong>
                    </>
                  )}
                </div>

                <ul className="mt-5 space-y-2 flex-1">
                  {(plan.highlights ?? []).map((h: string, i: number) => {
                    // Durante a promo, risca os bullets de taxa (ex: "Taxa reduzida: 10%").
                    const isFeeHighlight = inWaiver && /taxa.*\d+%/i.test(h);
                    return (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <span className="text-teal-500 shrink-0">✓</span>
                        <span className={isFeeHighlight ? 'line-through text-slate-400' : ''}>{h}</span>
                      </li>
                    );
                  })}
                </ul>

                <div className="mt-6 pt-2">
                  {isCurrent ? (
                    <Button disabled variant="outline" className="w-full">Plano atual</Button>
                  ) : tier === 'FREE' ? (
                    <Button
                      onClick={() => manage.mutate()}
                      disabled={manage.isPending || !hasActive}
                      variant="outline"
                      className="w-full"
                    >
                      {hasActive ? 'Voltar ao gratuito' : 'Gratuito'}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => upgrade.mutate(tier as 'PRO' | 'PREMIUM')}
                      disabled={upgrade.isPending}
                      className={cn(
                        'w-full',
                        plan.highlighted
                          ? 'bg-teal-500 hover:bg-teal-600 text-white'
                          : 'bg-coral-400 hover:bg-coral-500 text-white',
                      )}
                    >
                      {upgrade.isPending ? 'Carregando...' : `Assinar ${plan.name}`}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {(upgrade.isError || manage.isError) && (
        <p className="text-sm text-red-600">Algo deu errado. Tente novamente.</p>
      )}

      <p className="text-xs text-slate-400 text-center">
        Pagamento processado de forma segura. Você pode cancelar a qualquer momento.
      </p>
    </div>
  );
}
